import {
  CrewHourEntry,
  DailySiteReportSections,
  DocumentItem,
  IncidentItem,
  NoteItem,
  StatusChangeItem,
  TaskItem,
  WarehouseTx,
} from '@models/dailySiteReport.model';

/** Sprint 38, story-206 (D7). One card per project; empty sections are simply absent (empty arrays). */
export interface ProjectCardModel {
  /** Stable React key; `general` for items with no project. */
  key: string;
  projectId: string | null;
  projectName: string | null;
  statusChanges: StatusChangeItem[];
  incidents: IncidentItem[];
  crewHours: CrewHourEntry[];
  notes: NoteItem[];
  documents: DocumentItem[];
}

export interface LookingAheadGroup {
  key: string;
  projectId: string | null;
  projectName: string | null;
  tasksStarting: TaskItem[];
  tasksDue: TaskItem[];
  pendingIncidents: IncidentItem[];
}

export interface GroupedReport {
  cards: ProjectCardModel[];
  /** The report-level "tomorrow" block, grouped by project. Null when absent or fully empty. */
  lookingAhead: { tomorrowISO: string; groups: LookingAheadGroup[] } | null;
  stock: WarehouseTx[];
  truncated: { statusChanges: boolean; incidents: boolean; notes: boolean; documents: boolean };
  /** True when there is nothing at all to render. */
  isEmpty: boolean;
}

const GENERAL_KEY = 'general';

const keyOf = (projectId: string | null | undefined): string => (projectId ? projectId : GENERAL_KEY);

/**
 * Regroups the stored, section-first payload by project (the payload itself is unchanged).
 * Section order inside a card follows the email; cards are ordered by project name with the
 * project-less "General" card last. Tolerates old payloads (missing optional fields, absent
 * sections) and never reads a cost field.
 */
export function groupReportByProject(s: DailySiteReportSections): GroupedReport {
  const cards = new Map<string, ProjectCardModel>();
  const cardFor = (projectId: string | null | undefined, projectName: string | null | undefined): ProjectCardModel => {
    const key = keyOf(projectId);
    let card = cards.get(key);
    if (!card) {
      card = {
        key,
        projectId: projectId || null,
        projectName: projectId ? (projectName ?? null) : null,
        statusChanges: [],
        incidents: [],
        crewHours: [],
        notes: [],
        documents: [],
      };
      cards.set(key, card);
    } else if (!card.projectName && projectName && projectId) {
      card.projectName = projectName;
    }
    return card;
  };

  (s.statusChanges?.items ?? []).forEach((i) => cardFor(i.projectId, i.projectName).statusChanges.push(i));
  (s.incidents?.items ?? []).forEach((i) => cardFor(i.projectId, i.projectName).incidents.push(i));
  (s.crewHours?.projects ?? []).forEach((p) => {
    if (p.entries.length > 0) cardFor(p.projectId, p.projectName).crewHours.push(...p.entries);
  });
  (s.notes?.items ?? []).forEach((i) => cardFor(i.projectId, i.projectName).notes.push(i));
  (s.documents?.items ?? []).forEach((i) => cardFor(i.projectId, i.projectName).documents.push(i));

  const orderedCards = Array.from(cards.values()).sort((a, b) => {
    if (a.key === GENERAL_KEY) return 1;
    if (b.key === GENERAL_KEY) return -1;
    return (a.projectName ?? '').localeCompare(b.projectName ?? '');
  });

  const groups = new Map<string, LookingAheadGroup>();
  const groupFor = (projectId: string | null | undefined, projectName: string | null | undefined) => {
    const key = keyOf(projectId);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        projectId: projectId || null,
        projectName: projectId ? (projectName ?? null) : null,
        tasksStarting: [],
        tasksDue: [],
        pendingIncidents: [],
      };
      groups.set(key, group);
    }
    return group;
  };
  const la = s.lookingAhead;
  la?.tasksStarting.forEach((t) => groupFor(t.projectId, t.projectName).tasksStarting.push(t));
  la?.tasksDue.forEach((t) => groupFor(t.projectId, t.projectName).tasksDue.push(t));
  la?.pendingIncidents.forEach((i) => groupFor(i.projectId, i.projectName).pendingIncidents.push(i));
  const orderedGroups = Array.from(groups.values()).sort((a, b) => {
    if (a.key === GENERAL_KEY) return 1;
    if (b.key === GENERAL_KEY) return -1;
    return (a.projectName ?? '').localeCompare(b.projectName ?? '');
  });

  const stock = (s.stock?.warehouses ?? []).filter((w) => w.entries.length > 0);
  const lookingAhead = la && orderedGroups.length > 0 ? { tomorrowISO: la.tomorrowISO, groups: orderedGroups } : null;

  return {
    cards: orderedCards,
    lookingAhead,
    stock,
    truncated: {
      statusChanges: Boolean(s.statusChanges?.truncated),
      incidents: Boolean(s.incidents?.truncated),
      notes: Boolean(s.notes?.truncated),
      documents: Boolean(s.documents?.truncated),
    },
    isEmpty: orderedCards.length === 0 && stock.length === 0 && lookingAhead === null,
  };
}
